<i18n>
{
  "de": {
    "Greeting": "Hallo, {name}!",
    "Language": "Sprache",
    "Settings": "Einstellungen",
    "My Page": "Meine Seite",
    "Purchases": "Käufe",
    "Sign out": "Abmelden",
    "Sign in or sign up": "Anmelden oder registrieren",
    "Language de": "Deutsch",
    "Language en": "Englisch",
    "No new notifications": "Keine neuen Benachrichtigungen",
    "title": {
      "onboarding": "Onboarding"
    },
    "notifications": {
      "onboarding": {
        "payment": "Deine Zahlungsverbindung wurde noch nicht hergestellt.",
        "payment-requirement": "Für Deine Zahlungsanbindung sind weitere Angaben erforderlich."
      }
    }
  },
  "en": {
    "Greeting": "Hello, {name}!",
    "Language": "Language",
    "Settings": "Settings",
    "My Page": "My Page",
    "Purchases": "Purchases",
    "Sign out": "Sign out",
    "Sign in or sign up": "Sign in or sign up",
    "Language de": "German",
    "Language en": "English",
    "No new notifications": "No new notifications",
    "title": {
      "onboarding": "Onboarding"
    },
   "notifications": {
      "onboarding": {
        "payment": "Your payment connection has not been established yet.",
        "payment-requirement": "Your payment connection requires further information."
      }
    }
  }
}
</i18n>

<template>
  <div>
    <div id="userMenu">
      <button
        class="user-menu-button"
        aria-label="User menu"
        aria-haspopup="true"
        @click.prevent.stop="toggleDropdown"
      >
        <img
          v-if="avatar"
          class="user-menu-avatar"
          :src="avatar"
          alt="Avatar"
        >
        <user-icon
          v-else
          class="user-menu-icon"
        />
        <span
          v-if="messagesLength > 0"
          class="user-menu-badge"
        >
          {{ messagesLength < 100 ? messagesLength : '99+' }} </span>
      </button>

      <transitionDropDown>
        <div
          v-show="dropDownOpen"
          class="user-dropdown-menu"
        >
          <div
            class="user-dropdown-content"
            role="menu"
            aria-orientation="vertical"
            aria-labelledby="user-menu"
          >
            <div
              v-if="isAuthenticated"
              class="user-dropdown-item is-flex is-justify-content-space-between is-align-items-center"
              role="menuitem"
            >
              <div>{{ $t('Greeting', { name: user.nickname }) }}</div>
              <a
                v-if="user.artist"
                :href="`/${user.urlAlias}`"
                class="profile-link"
              >{{ $t('My Page') }}</a>
            </div>

            <div
              v-if="isAuthenticated && !messages.length"
              class="user-dropdown-item"
              role="menuitem"
            >
              <div class="p-2 has-text-centered has-text-grey">
                {{ $t('No new notifications') }}
              </div>
            </div>

            <div
              v-if="isAuthenticated && messages.length"
              class="user-dropdown-item-container"
              :class="{ 'scrollable': messages.length > 3 }"
              role="menuitem"
            >
              <div
                v-for="message in systemMessages"
                :key="message._id"
                class="user-dropdown-item"
              >
                <div
                  class="p-2 is-flex is-align-items-flex-start is-justify-content-space-between cursor-pointer"
                  @click.stop="openSystemNotification(message)"
                >
                  <figure class="image mr-3">
                    <warn-icon
                      v-if="message.level === 'warn'"
                      class="dummy-avatar-icon"
                    />
                  </figure>
                  <div class="content is-flex-grow-1">
                    <strong class="has-text-grey-dark">{{ $t(`title.${message.message.topic}`) }}</strong>
                    <p class="has-text-grey">
                      {{ $t(message.message.text) }}
                    </p>
                  </div>
                </div>
              </div>

              <div
                v-for="message in postComments"
                :key="message.comment._id"
                class="user-dropdown-item"
              >
                <div
                  class="p-2 is-flex is-align-items-flex-start is-justify-content-space-between cursor-pointer"
                  @click.stop="openNotification(message)"
                >
                  <figure class="image mr-3">
                    <user-icon
                      v-if="!message.comment.avatarMediaId"
                      class="dummy-avatar-icon"
                    />
                    <img
                      v-else
                      class="is-rounded"
                      :src="`/services/media/image/${message.comment.user}/${message.comment.avatarMediaId}/32/32/image.webp`"
                      alt="User Avatar"
                    >
                  </figure>
                  <div class="content is-flex-grow-1">
                    <strong class="has-text-grey-dark">{{ message.comment.nickname }}</strong>
                    <p class="has-text-grey">
                      {{ message.comment.contentShort }}
                    </p>
                  </div>
                  <button
                    class="delete"
                    aria-label="Remove Notification"
                    @click.prevent.stop="removeNotification(message)"
                  />
                </div>
              </div>
            </div>
            <div
              class="user-dropdown-item cursor-pointer"
              role="menuitem"
              @click.stop.prevent="toggleLanguage"
            >
              <div class="is-flex is-justify-content-space-between is-align-items-center">
                <span>{{ $t('Language') }}</span>
                <span
                  :class="{ 'dropdown-arrow-down': showLanguage, 'dropdown-arrow-right': !showLanguage }"
                  class="dropdown-arrow"
                />
              </div>
              <div
                v-show="showLanguage"
                class="pt-5"
              >
                <div class="is-flex is-justify-content-space-evenly mt-10">
                  <div
                    v-for="locale in $i18n.locales"
                    :key="locale.code"
                    class="cursor-pointer language-button"
                  >
                    <button
                      :title="$t(`Language ${locale.code}`)"
                      @click.prevent="switchLanguage(locale)"
                    >
                      <flags-icon :countryKey="locale.code" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="isAuthenticated && followersAvailable"
              class="user-dropdown-item cursor-pointer"
              role="menuitem"
              @click.prevent.stop="toggleFollowship"
            >
              <div class="is-flex is-justify-content-space-between is-align-items-center">
                <span>{{ $t('Following') }}</span>
                <span
                  :class="{ 'dropdown-arrow-down': showFollowship, 'dropdown-arrow-right': !showFollowship }"
                  class="dropdown-arrow"
                />
              </div>
              <div
                v-show="showFollowship"
                class="pt-10"
              >
                <follow-box />
              </div>
            </div>
            <div
              class="user-dropdown-item"
              role="menuitem"
            >
              <div class="button-row">
                <a
                  v-if="isAuthenticated"
                  href="/apps/dashboard"
                  class="button-row-item"
                >
                  {{ $t('Settings') }}
                </a>
                <a
                  v-if="isAuthenticated"
                  href="/apps/dashboard/purchases/"
                  class="button-row-item"
                >
                  {{ $t('Purchases') }}
                </a>
                <button
                  v-if="isAuthenticated"
                  class="button-row-item sign-button"
                  @click.stop.prevent="doLogout"
                >
                  {{ $t('Sign out') }}
                </button>
                <button
                  v-if="!isAuthenticated"
                  class="button-row-item sign-button"
                  @click.stop.prevent="doLogin"
                >
                  {{ $t('Sign in or sign up') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </transitionDropDown>
    </div>
    <div
      v-if="dropDownOpen"
      id="menuBackdrop"
      @click.prevent="closeDropdown"
    />
    <modal
      v-if="modalOpen"
      :backdropClose="true"
      :defaultFooter="true"
      @close="modalOpen = false"
    >
      <template #header>
        <h1 class="title">
          {{ $t('title.onboarding') }}
        </h1>
      </template>
      <template #body>
        <div class="is-overlay">
          <section>
            <div class="content">
              <div class="">
                <pre>{{ modalMessage }}</pre>
                {{ $t('notifications.onboarding.payment') }}
              </div>
            </div>
          </section>
        </div>
      </template>
      <template #footer>
        <button
          class="button is-primary"
          @click="modalOpen = false"
        >
          {{ $t('close') }}
        </button>
      </template>
    </modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import transitionDropDown from '~/components/transitions/dropDown.vue'
import userIcon from '~/components/user-icon.vue'
import warnIcon from '~/components/warn-icon.vue'
import flagsIcon from '~/components/flags-icon.vue'
import followBox from '~/components/follow-box'
import modal from '~/components/modal.vue'

export default {
  name: 'userMenu',
  components: {
    flagsIcon,
    followBox,
    modal,
    transitionDropDown,
    userIcon,
    warnIcon
  },
  data: () => ({
    dropDownOpen: false,
    modalMessage: null,
    modalOpen: false,
    showFollowship: false,
    showLanguage: false
  }),

  mounted() {
    this.$root.$on('headerDropdownOpened', this.headerDropdownOpened)
  },

  computed: {
    ...mapState(['user', 'isAuthenticated', 'followship']),
    ...mapState('info', ['messages']),
    messagesLength() {
      return this.messages.length
    },
    systemMessages() {
      return this.messages.filter(message => message.target === 'system')
    },
    postComments() {
      return this.messages.filter(message => message.target === 'post')
    },
    avatar() {
      if (
        !this.isAuthenticated ||
        !this.user ||
        !this.user.profile ||
        !this.user.profile.imgs.avatar ||
        !this.user.profile.imgs.avatar.name
      ) {
        return null
      }
      const userId = this.user.id
      const name = this.user.profile.imgs.avatar.name
      return `/services/media/image/${userId}/${name}/64/0/image.jpeg`
    },
    followersAvailable() {
      return this.followship &&
        this.followship.users &&
        this.followship.users.length > 0
    }
  },
  methods: {
    ...mapActions(['logout', 'setInterfaceLanguage', 'markAsReadComment']),
    ...mapActions('info', ['removeCommentNotifications']),
    toggleDropdown() {
      this.dropDownOpen = !this.dropDownOpen
      if (this.dropDownOpen) {
        this.$root.$emit('headerDropdownOpened', 'user')
      }
    },
    closeDropdown() {
      this.dropDownOpen = false
    },
    headerDropdownOpened(menu) {
      if (menu !== 'user') {
        this.closeDropdown()
      }
    },
    toggleFollowship() {
      this.showFollowship = !this.showFollowship
    },
    toggleLanguage() {
      this.showLanguage = !this.showLanguage
    },
    async switchLanguage(locale) {
      await this.setInterfaceLanguage(locale.code)
      if (process.client && window) {
        window.location.href = this.switchLocalePath(locale.code)
      }
      this.closeDropdown()
    },
    async openNotification(message) {
      this.closeDropdown()
      if (!message.target === 'post') {
        console.error('Cannot open notification. Type not implemented.')
        return
      }
      const locale = await this.$i18n.locale
      this.$router.push({
        name: `id-post-slug-postId___${locale}`,
        params: {
          id: message.comment.artist.urlAlias,
          slug: message.comment.post.urlSlug,
          postId: message.comment.post._id,
          itemType: 'comment',
          itemId: message.comment._id
        }
        // query: { comments: 'init' }
      })
    },
    async openSystemNotification(message) {
      this.closeDropdown()
      if (!message.target === 'system') {
        console.error('Cannot open notification. Type not implemented.')
        return
      }
      if (message.message.topic === 'onboarding') {
        window.location.href = '/apps/dashboard/onboarding'
        return
      }

      this.modalMessage = message
      this.modalOpen = true
    },
    removeNotification(message) {
      this.removeCommentNotifications([message.comment._id])
      this.$store.dispatch('websocket/sendMessage', {
        type: 'readComments',
        commentIds: [message.comment._id]
      })
    },
    doLogin() {
      this.$root.$emit('showLogin')
      this.closeDropdown()
    },
    async doLogout() {
      const data = await this.apiGet('/api/private/logout')
      if (data) {
        this.logout()
        this.$root.$emit('refreshAPI')
      }
      this.closeDropdown()
    }
  },
  beforeDestroy() {
    this.$root.$off('headerDropdownOpened', this.headerDropdownOpened)
  }
}
</script>

<style
  lang="scss"
  scoped
>
@import 'assets/sass/_variables';

#menuBackdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999;
}

#userMenu {
  margin-left: 16px;
  padding-top: 3px;
  padding-right: 7px;
  position: relative;

  .user-menu-button {
    display: flex;
    font-size: 0.875rem;
    padding: 2px;
    border: 2px solid transparent;
    border-radius: 9999px;
    transition: border-color 150ms ease-in-out;
    outline: none;
    cursor: pointer;
    background-color: transparent;
    position: relative;

    &:focus {
      border-color: $primary;
    }

    .user-menu-icon {
      height: 2.4rem;
      width: 2.4rem;
      color: var(--tertiary-text-color);
    }

    .user-menu-avatar {
      height: 2.2rem;
      width: 2.2rem;
      border-radius: 9999px;
    }

    .user-menu-badge {
      position: absolute;
      top: 10px;
      right: 5px;
      transform: translate(50%, -50%);
      font-size: 0.75rem !important;
      background-color: $primary;
      color: white;
      font-size: 0.75rem;
      font-weight: bold;
      border-radius: 50%; // Ensures the badge is always circular
      width: 1.4rem; // Fixed width for a circle
      height: 1.4rem; // Fixed height for a circle
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}

$text-gray: #4A5568;
$bg-hover: #F7FAFC;

.user-dropdown-menu {
  position: absolute;
  z-index: 1000;
  right: 0;
  margin-top: 0.5rem;
  width: 24rem;
  border-radius: 0.375rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06);
  transform-origin: top right;
  display: block;

  .user-dropdown-content {
    padding: 0.25rem 0;
    border-radius: 0.375rem;
    background-color: white;
    border: 1px solid rgba(0, 0, 0, 0.05);

    .user-dropdown-item {
      display: block;
      padding: 0.5rem 1rem;
      font-size: 0.875rem;
      line-height: 1.25rem;
      color: $text-gray;
      text-decoration: none;
      transition: background-color 150ms ease-in-out;

      &:hover,
      &:focus {
        background-color: $bg-hover;
        outline: none;
      }

      .image {
        flex: 0 0 auto;
        width: 32px;
        height: 32px;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      .dummy-avatar-icon {
        color: $text-gray;
      }

      .delete {
        font-size: 1rem;
        padding: 1rem;
        margin-left: 0.5rem;
        color: var(--primary-color);
        border-radius: 50%;
        cursor: pointer;

        &:hover {
          background-color: var(--primary-color);
          color: var(--primary-text-color);
        }
      }
    }
  }
}

.user-dropdown-item-container {
  max-height: 300px; // Set the maximum height for the container
  overflow-y: auto; // Enable vertical scrolling if content exceeds max height
  overscroll-behavior: contain;

  // Style the scrollbar
  scrollbar-width: thin; // For Firefox
  scrollbar-color: $primary transparent; // For Firefox

  &::-webkit-scrollbar {
    width: 8px; // Set scrollbar width
  }

  &::-webkit-scrollbar-thumb {
    background-color: $primary; // Set scrollbar thumb color
    border-radius: 4px; // Round the edges of the scrollbar thumb
  }

  &::-webkit-scrollbar-track {
    background: transparent; // Set scrollbar track background
  }

  // Add shadow to indicate scrollability
  &.scrollable {
    box-shadow: inset 0 -6px 10px -4px rgba(0, 0, 0, 0.2); // More noticeable shadow at the bottom
  }
}

.cursor-pointer {
  cursor: pointer;
}

.button-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem; // Add gaps between buttons
  background-color: white;
  border-radius: 0.375rem;

  .button-row-item {
    flex: 1; // Ensure buttons take equal space
    text-align: center; // Center-align text within buttons
    font-size: 0.875rem;
    color: $text-gray;
    text-decoration: none;
    transition: background-color 150ms ease-in-out, color 150ms ease-in-out;
    cursor: pointer;

    &:hover {
      background-color: $bg-hover;
      color: $primary;
    }
  }
}

.sign-button {
  color: var(--primary-color);
  background-color: transparent;
  border: none;
  line-height: 20px;
  cursor: pointer;

  &:hover {
    color: var(--primary-text-color);
    background-color: var(--primary-color);
  }

  &:focus {
    outline: auto;
  }
}

.dropdown-arrow {
  display: inline-block;
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;

  &.dropdown-arrow-right {
    border-top: 5px solid $text-gray;
  }

  &.dropdown-arrow-down {
    border-top: none;
    border-bottom: 5px solid $text-gray;
  }
}

.language-button button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  border: 2px solid transparent;
  border-radius: 50%;
  background-color: white;
  transition: all 150ms ease-in-out;
  cursor: pointer;

  &:hover {
    border-color: $primary;
    transform: scale(1.1);
  }

  &:focus {
    outline: none;
    border-color: $primary;
  }
}

.profile-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  background: transparent;
  color: $primary;
  text-decoration: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  box-shadow: none;
  transition: all 200ms ease-in-out;
  border: 1px solid $primary;
  min-width: 80px;
  text-align: center;

  &:hover {
    background: $primary;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    color: white;
    text-decoration: none;
  }

  &:focus {
    outline: none;
    border-color: $primary;
    box-shadow: 0 0 0 2px rgba($primary, 0.3);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
}
</style>
